'use client';

import type { HeaderLabels, NavCategory } from './header-types';
import type { KeyboardEvent, MouseEvent } from 'react';

import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useRef, useState } from 'react';

const panelOpenOnHoverClassName =
  'group-hover/item:visible group-hover/item:opacity-100 group-hover/item:[transform:none] group-hover/item:[transition-delay:.08s,.08s,0s]';
const panelOpenOnFocusClassName =
  'group-focus-within/item:visible group-focus-within/item:opacity-100 group-focus-within/item:[transform:none] group-focus-within/item:[transition-delay:.08s,.08s,0s]';

function blurActiveElement() {
  if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
}

type CategoryMegaMenuProps = {
  categories: NavCategory[];
  labels: Pick<HeaderLabels, 'categories' | 'showAll'>;
};

export function CategoryMegaMenu({ categories, labels }: CategoryMegaMenuProps) {
  return (
    <nav
      aria-label={labels.categories}
      className="hidden border-t border-[#e6eaee] min-[901px]:block"
    >
      <div className="custom-section">
        <ul className="custom-container custom-container--wide flex justify-between">
          {categories.map(category => (
            <CategoryMegaMenuItem
              key={category.id}
              category={category}
              showAllLabel={labels.showAll}
            />
          ))}
        </ul>
      </div>
    </nav>
  );
}

function CategoryMegaMenuItem({
  category,
  showAllLabel,
}: {
  category: NavCategory;
  showAllLabel: string;
}) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [hasIntent, setHasIntent] = useState(false);
  // The panel opens on hover/focus; after a client-side navigation the pointer is still over it,
  // so it stays closed until the pointer leaves or the item is entered again.
  const [isDismissed, setIsDismissed] = useState(false);
  const topLinkRef = useRef<HTMLAnchorElement>(null);
  const subcategoryListRef = useRef<HTMLUListElement>(null);
  const panesRef = useRef<HTMLDivElement>(null);
  const subcategories = category.children;

  const restore = () => {
    setIsDismissed(false);
    setHasIntent(true);
  };
  const dismiss = (event: MouseEvent<HTMLAnchorElement>) => {
    // Modified clicks open a new tab or window, so the menu stays as it is.
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }
    setIsDismissed(true);
    blurActiveElement();
  };
  const handleKeyDown = (event: KeyboardEvent<HTMLLIElement>) => {
    if (event.key !== 'Escape') return;
    topLinkRef.current?.focus();
    setIsDismissed(true);
  };

  // Only the active panel is rendered visibly, so Tab hops between a subcategory and its panel
  // instead of running through the whole subcategory list first.
  const subcategoryLink = (index: number) =>
    subcategoryListRef.current?.querySelectorAll<HTMLAnchorElement>(':scope > li > a')[index];
  const handleSubcategoryKeyDown = (event: KeyboardEvent<HTMLAnchorElement>, index: number) => {
    const firstPaneLink = panesRef.current?.children[index]?.querySelector('a');
    if (event.key !== 'Tab' || event.shiftKey || !firstPaneLink) return;
    event.preventDefault();
    firstPaneLink.focus();
  };
  const handlePaneKeyDown = (event: KeyboardEvent<HTMLDivElement>, index: number) => {
    if (event.key !== 'Tab') return;
    const links = event.currentTarget.querySelectorAll('a');
    const edge = event.shiftKey ? links[0] : links[links.length - 1];
    const target = subcategoryLink(event.shiftKey ? index : index + 1);
    if (event.target !== edge || !target) return;
    event.preventDefault();
    target.focus();
  };

  return (
    <li
      className="group/item"
      onMouseEnter={restore}
      onMouseLeave={restore}
      onFocus={restore}
      onKeyDown={handleKeyDown}
    >
      <Link
        ref={topLinkRef}
        href={category.href}
        prefetch={false}
        onClick={dismiss}
        className="flex h-[52px] items-center gap-2 whitespace-nowrap border-b-[2.5px] border-transparent px-1.5 text-[.85rem] font-semibold [transition:border-color_.15s,color_.15s] group-focus-within/item:border-[#00a1e6] group-focus-within/item:text-[#00a1e6] group-hover/item:border-[#00a1e6] group-hover/item:text-[#00a1e6] min-[1101px]:px-2.5 min-[1101px]:text-[.92rem]"
      >
        {category.iconUrl && (
          <Image
            src={category.iconUrl}
            alt=""
            width={20}
            height={20}
            className="hidden min-[1101px]:block"
          />
        )}
        {category.name}
      </Link>
      {subcategories.length > 0 && (
        <div
          className={cn(
            'invisible absolute inset-x-0 top-full border-t border-[#e6eaee] bg-white opacity-0 shadow-[0_30px_50px_-20px_rgba(0,0,0,.25)]',
            '[transform:translateY(6px)] [transition:opacity_.18s,transform_.18s,visibility_0s_.18s]',
            panelOpenOnHoverClassName,
            panelOpenOnFocusClassName,
            isDismissed && '!invisible !opacity-0',
          )}
        >
          <div className="custom-section">
            <div className="custom-container custom-container--wide grid max-h-[70vh] grid-cols-[340px_1fr] gap-6 pb-6 pt-5">
              <ul
                ref={subcategoryListRef}
                className="overflow-y-auto border-r border-[#e6eaee] pr-3"
              >
                {subcategories.map((subcategory, index) => {
                  const isActive = index === activeIndex;

                  return (
                    <li key={subcategory.id}>
                      <Link
                        href={subcategory.href}
                        prefetch={false}
                        onClick={dismiss}
                        onMouseEnter={() => setActiveIndex(index)}
                        onFocus={() => setActiveIndex(index)}
                        onKeyDown={event => handleSubcategoryKeyDown(event, index)}
                        className={cn(
                          'flex items-center gap-3 rounded-xl px-2.5 py-[5px] text-[.9rem] font-medium hover:bg-[#f3f5f7] hover:text-[#00a1e6]',
                          isActive && 'bg-[#f3f5f7] text-[#00a1e6]',
                        )}
                      >
                        {subcategory.iconUrl && hasIntent ? (
                          <Image
                            src={subcategory.iconUrl}
                            alt=""
                            width={36}
                            height={36}
                            sizes="36px"
                            fetchPriority="low"
                            className="h-9 w-9 flex-none rounded-[10px] bg-[#f3f5f7] object-contain p-[3px]"
                          />
                        ) : (
                          <i className="h-9 w-9 flex-none rounded-[10px] bg-[#f3f5f7]" />
                        )}
                        <span className="flex-1">{subcategory.name}</span>
                        <b
                          aria-hidden
                          className={cn(
                            'text-[1.2rem] font-normal text-[#b0b9c2]',
                            isActive && 'text-[#00a1e6]',
                          )}
                        >
                          ›
                        </b>
                      </Link>
                    </li>
                  );
                })}
              </ul>
              <div ref={panesRef} className="relative flex flex-col overflow-y-auto">
                {subcategories.map((subcategory, index) => (
                  <div
                    key={subcategory.id}
                    hidden={index !== activeIndex}
                    onKeyDown={event => handlePaneKeyDown(event, index)}
                  >
                    <div className="mb-3.5 flex items-baseline justify-between gap-4">
                      <div data-ea-visual-tag="h4" className="text-[1.3rem] font-extrabold">
                        {subcategory.name}
                      </div>
                      <Link
                        href={subcategory.href}
                        prefetch={false}
                        onClick={dismiss}
                        className="text-[.9rem] font-semibold text-[#00a1e6]"
                      >
                        {showAllLabel} <span aria-hidden>→</span>
                      </Link>
                    </div>
                    <ul className="[column-gap:24px] [columns:3_200px]">
                      {subcategory.children.map(link => (
                        <li key={link.id} className="break-inside-avoid">
                          <Link
                            href={link.href}
                            prefetch={false}
                            onClick={dismiss}
                            className="block border-b border-[#e6eaee] py-[7px] text-[.92rem] text-[#3a444d] hover:text-[#00a1e6]"
                          >
                            {link.name}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
                <Link
                  href={category.href}
                  prefetch={false}
                  onClick={dismiss}
                  className="mt-auto self-start pt-4 text-[.9rem] font-bold text-[#00a1e6]"
                >
                  {showAllLabel}: {category.name} <span aria-hidden>→</span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </li>
  );
}
